import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import Select, { components } from "react-select";
import { CheckCheck } from "lucide-react";

import StyledModal from "../../Modal/StyledModal";
import BlobLoader from "../../Loader/BlobLoader";
import { handleCatch } from "@/shared/helpers";
import {
  buildReviewPayload,
  fetchReviewOutcomeOptions,
  parsePrescriptionReviewError,
  submitPrescriptionReview,
} from "@/services/prescriptionReview";
import { ReactComponent as DropdownArrowDown } from "@/assets/icons/dropdownArrowDown.svg";
import { ReactComponent as DropdownArrowUp } from "@/assets/icons/dropdownArrowUp.svg";

const selectStyles = (hasError) => ({
  menuPortal: (base) => ({ ...base, zIndex: 60 }),
  control: (base) => ({
    ...base,
    backgroundColor: "transparent",
    borderRadius: 0,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: hasError ? "#e41818" : "#c8c8c8",
    boxShadow: "none",
    minHeight: 42,
    paddingLeft: 10,
    paddingRight: 10,
  }),
  menu: (base) => ({ ...base, zIndex: 60 }),
  singleValue: (base) => ({
    ...base,
    color: "#646464",
    fontSize: 18,
    lineHeight: "22px",
    textAlign: "left",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#646464",
    fontSize: 17,
    textAlign: "left",
  }),
});

function DropdownIndicator(props) {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen ? <DropdownArrowUp /> : <DropdownArrowDown />}
      </components.DropdownIndicator>
    )
  );
}

const PrescriptionReviewModal = ({
  open,
  prescriptionId,
  onClose,
  onSuccess,
}) => {
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [otherText, setOtherText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const resetForm = useCallback(() => {
    setSelectedOption(null);
    setOtherText("");
    setError("");
  }, []);

  useEffect(() => {
    if (!open) {
      resetForm();
      return undefined;
    }

    let cancelled = false;
    setOptionsLoading(true);
    setError("");

    fetchReviewOutcomeOptions()
      .then((list) => {
        if (cancelled) return;
        setOptions(list);
        if (!list.length) {
          setError("No review outcome options are available.");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          handleCatch(err);
          setOptions([]);
          setError("Could not load review options.");
        }
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, resetForm]);

  const selectOptions = useMemo(
    () =>
      options.map((option) => ({
        value: option.id,
        label: option.name,
        isOther: option.isOther,
      })),
    [options]
  );

  const handleSubmit = async () => {
    if (!prescriptionId || submitting) return;

    if (!selectedOption) {
      setError("Please select a review outcome.");
      return;
    }

    if (selectedOption.isOther && !otherText.trim()) {
      setError("Please specify the other review outcome.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await submitPrescriptionReview(
        prescriptionId,
        buildReviewPayload({
          reviewOutcomeId: Number(selectedOption.value),
          reviewOutcomeOther: otherText,
          selectedOption: options.find(
            (option) => String(option.id) === String(selectedOption.value)
          ),
        })
      );
      await onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(parsePrescriptionReviewError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const busy = optionsLoading || submitting;

  return (
    <StyledModal>
      <Modal
        isOpen={open}
        onRequestClose={busy ? undefined : onClose}
        className="prescription-review-modal-content"
        contentLabel="Review Outcome"
        overlayClassName="overlay-modal overlay-modal--stacked"
        shouldCloseOnOverlayClick={!busy}
        ariaHideApp={false}
        data={{ testid: "paf-review-modal" }}
      >
        {busy && (
          <div
            className="prescription-review-modal-loader"
            data-testid="paf-review-modal-loader"
          >
            <BlobLoader size={96} showLoadingText={false} />
          </div>
        )}

        <div className="wrapper-modal" data-testid="paf-review-modal-content">
          <div className="review-outcome-icon">
            <span className="review-outcome-icon-circle" aria-hidden>
              <CheckCheck size={32} strokeWidth={2.5} />
            </span>
          </div>
          <div className="main-text-orient">Review Outcome</div>
          <div className="second-text">
            Kindly provide the review results for this PAF.
          </div>

          <div
            className="prescription-review-modal-field"
            data-testid="paf-review-modal-field"
          >
            <div data-testid="paf-review-modal-outcome-select">
              <Select
                className="w-full text-left"
                classNamePrefix="custom-select-review"
                components={{
                  DropdownIndicator,
                  IndicatorSeparator: () => null,
                }}
                value={selectedOption}
                onChange={(option) => {
                  setSelectedOption(option);
                  setError("");
                  if (!option?.isOther) setOtherText("");
                }}
                options={selectOptions}
                placeholder="Select"
                isDisabled={busy || selectOptions.length === 0}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                styles={selectStyles(Boolean(error && !selectedOption))}
                inputId="paf-review-outcome-select"
              />
            </div>

            {selectedOption?.isOther && (
              <div className="prescription-review-modal-other">
                <input
                  className="prescription-review-modal-other-input"
                  type="text"
                  value={otherText}
                  onChange={(e) => {
                    setOtherText(e.target.value);
                    setError("");
                  }}
                  disabled={busy}
                  placeholder="Please state"
                  data-testid="paf-review-modal-other-input"
                />
              </div>
            )}

            {error && (
              <p
                className="prescription-review-modal-error"
                role="alert"
                data-testid="paf-review-modal-error"
              >
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="wrapper-buttons" data-testid="paf-review-modal-actions">
          <button
            type="button"
            className="cancel mr-6"
            onClick={onClose}
            disabled={busy}
            data-testid="paf-review-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="green-button green-haze-background"
            onClick={handleSubmit}
            disabled={busy || !prescriptionId}
            data-testid="paf-review-modal-confirm"
          >
            {submitting ? "Submitting…" : "Complete Review"}
          </button>
        </div>
      </Modal>
    </StyledModal>
  );
};

PrescriptionReviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  prescriptionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default PrescriptionReviewModal;
